import {
  type SharedTextColumnGroup,
  reconcileSharedTextValues,
} from "@/lib/shared-map-list-column-filters";
import {
  columnFilterValuesEqual,
  withLinkedColumnValue,
} from "@/lib/sync-global-column-filters";
import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useRef,
} from "react";

export type SharedTextColumnBucket = {
  filters: Record<string, string[]>;
  setFilters: Dispatch<SetStateAction<Record<string, string[]>>>;
  columns: Partial<Record<SharedTextColumnGroup, string>>;
};

/**
 * Keep one text column filter identical on every list that displays it.
 * Licenses, cases, and short-term rentals each use their own column id.
 */
export function useSyncSharedTextColumns({
  buckets,
}: {
  buckets: readonly SharedTextColumnBucket[];
}) {
  useSyncSharedTextGroup("city", buckets);
  useSyncSharedTextGroup("state", buckets);
  useSyncSharedTextGroup("zip", buckets);
  useSyncSharedTextGroup("apn", buckets);
}

function useSyncSharedTextGroup(
  group: SharedTextColumnGroup,
  buckets: readonly SharedTextColumnBucket[],
) {
  const active = useMemo(
    () =>
      buckets.flatMap((bucket) => {
        const columnId = bucket.columns[group];
        return columnId ? [{ ...bucket, columnId }] : [];
      }),
    [buckets, group],
  );
  const readyRef = useRef(false);
  const prevRef = useRef<Array<string[] | undefined>>([]);
  const pendingRef = useRef<Array<string[] | undefined> | null>(null);

  useEffect(() => {
    if (active.length < 2) return;
    const values = active.map((bucket) => bucket.filters[bucket.columnId]);
    if (pendingRef.current) {
      const pending = pendingRef.current;
      const caughtUp =
        values.length === pending.length &&
        values.every((entry, index) =>
          columnFilterValuesEqual(entry, pending[index]),
        );
      if (!caughtUp) return;
      pendingRef.current = null;
      prevRef.current = pending;
    }

    const previous = prevRef.current;
    const initializing = !readyRef.current;
    const changed = values.map(
      (entry, index) =>
        previous.length !== values.length ||
        !columnFilterValuesEqual(entry, previous[index]),
    );
    if (
      !initializing &&
      previous.length === values.length &&
      !changed.some(Boolean)
    ) {
      return;
    }

    const next = reconcileSharedTextValues({
      values,
      changed,
      initializing,
    });
    readyRef.current = true;
    prevRef.current = next;

    let filtersChanged = false;
    active.forEach((bucket, index) => {
      const nextFilters = withLinkedColumnValue(
        bucket.filters,
        bucket.columnId,
        next[index],
      );
      if (nextFilters === bucket.filters) return;
      filtersChanged = true;
      const nextValues = next[index];
      bucket.setFilters((current) =>
        withLinkedColumnValue(current, bucket.columnId, nextValues),
      );
    });
    if (filtersChanged) pendingRef.current = next;
  }, [active]);
}
